import { QueryClient } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink, TRPCClientError } from '@trpc/client';
import { createTRPCContext } from '@trpc/tanstack-react-query';
import type { AppRouter } from '@uplink/api/router';
import { type Messages, ru } from './i18n/ru.ts';
import { authHeader, webSignIn } from './telegram.ts';

export const { TRPCProvider, useTRPC } = createTRPCContext<AppRouter>();

export const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${API_URL}/trpc`,
      headers() {
        const auth = authHeader();
        return auth ? { authorization: auth } : {};
      },
      // The website signs in with an HttpOnly session cookie
      fetch: (url, opts) => fetch(url, { ...opts, credentials: 'include' }),
    }),
  ],
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (count, err) => !(err instanceof TRPCClientError && err.data?.httpStatus < 500) && count < 2,
    },
  },
});

let messages: Messages['errors'] = ru.errors;
/** Set by PrefsProvider on every render, so errors follow the chosen language. */
export function setErrorMessages(m: Messages['errors']) {
  messages = m;
}

/** The server already writes user-facing messages for domain errors; everything else gets a generic line. */
export function errorText(err: unknown): string {
  if (err instanceof TRPCClientError && err.data?.domainCode) return err.message;
  const fields = err instanceof TRPCClientError ? err.data?.zodError?.fieldErrors : null;
  if (fields) return messages.fields;
  if (err instanceof TRPCClientError && err.data?.httpStatus === 401)
    return webSignIn ? messages.signedOut : messages.openInTelegram;
  return messages.network;
}

/** Имена полей, которые сервер отклонил при проверке формы. */
export function errorFields(err: unknown): ReadonlySet<string> {
  const fields =
    err instanceof TRPCClientError
      ? (err.data?.zodError?.fieldErrors as Record<string, string[]> | undefined)
      : undefined;
  return new Set(fields ? Object.keys(fields) : []);
}
